<template>
  <page-listado>
    <page-header title="Ofertas">
      <b-col md="2" class="text-right">
        <b-button variant="primary" size="sm" to="/ofertas/crear">
          <v-icon name="plus" width="16" />
          Crear oferta
        </b-button>
      </b-col>
    </page-header>

    <filtros-ofertas v-show="mostrarFiltros" ref="filtros" @filtrar="filtrosCambiados" />
    <productividad-comercial ref="productividad" />

    <div class="mt-4 mb-2" style="display: flex; align-items: flex-end; gap: 1rem">
      <div style="min-width: 500px">
        <b>Estados</b>
        <combo-multiple
          placeholder="Seleccione uno o más estados"
          v-model="filtros.estados"
          :options="maestros.estados"
          class="input-mw filter"
        />
      </div>
      <div>
        <b-button size="sm" variant="white" @click="toggleColumnConfig">
          <b-icon icon="table" />
          Configurar columnas
        </b-button>
      </div>
    </div>

    <!-- Configuración de columnas visibles -->
    <div v-show="showColumnConfig">
      <div class="alert alert-warning mt-2 mb-4">
        <b-form-checkbox-group v-model="columnasVisibles" switches :options="columnOptions" />
        <hr />
        <div class="text-right">
          <small><i>* esta configuración se guarda en tu navegador y no afecta a otros usuarios.</i></small>
          <b-button variant="primary" size="sm" @click="resetearColumnasVisibles">Reset</b-button>
        </div>
      </div>
    </div>

    <!-- Filtros usados -->
    <div v-show="mostrarFiltrosUsados" class="mt-4 alert alert-warning">
      Filtrando por <b>{{ filtrosUsados }}</b> (<a href="javascript:;" @click="limpiarFiltros">Limpiar filtros</a>)
    </div>

    <tabla-ofertas
      :ofertas="resultado.ofertas"
      :columnas-visibles="columnasVisibles"
      @ofertaCambiada="ofertaCambiada"
    />

    <b-row class="mt-4">
      <b-col>
        <b-button to="/papelera" title="Papelera">
          <v-icon name="trash" width="16" />
          <span>Papelera</span>
        </b-button>
      </b-col>
      <b-col>
        <paginacion
          :page-count="parseInt(resultado.numeroPaginas)"
          :page="parseInt(resultado.pagina)"
          :total-items="parseInt(resultado.totalItems)"
        />
      </b-col>
    </b-row>
  </page-listado>
</template>

<script>
import { mapState, mapActions, mapWritableState } from "pinia";
import { useSessionStore } from "@/stores/SessionStore";
import { useFiltrosOfertaFormStore } from "@/stores/FiltrosOfertaForm";

import * as ofertasApi from "@/services/ofertas";

import TablaOfertas from "./Listar/TablaOfertas.vue";
import FiltrosOfertas from "./Listar/FiltrosOfertas.vue";
import ProductividadComercial from "./Listar/ProductividadComercial.vue";

function columnasVisiblesPorDefecto() {
  return ["campania", "numDias", "version", "estado", "fecha", "propietario"];
}

export default {
  components: {
    TablaOfertas,
    FiltrosOfertas,
    ProductividadComercial,
  },
  data() {
    return {
      mostrarFiltros: true,
      showColumnConfig: false,
      columnOptions: [
        { value: "cliente", text: "Cliente" },
        { value: "campania", text: "Campaña" },
        { value: "sector", text: "Sector" },
        { value: "numDias", text: "Días de gestión" },
        { value: "estado", text: "Estado" },
        { value: "pipe", text: "Pipedrive" },
        { value: "version", text: "Versión" },
        { value: "fecha", text: "Fecha Oferta" },
        { value: "fechaFirmaContrato", text: "F/Firma" },
        { value: "diasParaFirmaContrato", text: "Días/Firma" },
        { value: "propietario", text: "Propietario" },
        { value: "plan", text: "Plan" },
        { value: "formaPagoCuota", text: "F/Pago" },
        { value: "duracionContrato", text: "D/Contr." },
        { value: "info", text: "Info" },
        { value: "totalCuotaAnualEnEuros", text: "€ Cuota" },
        { value: "totalServiciosEnEuros", text: "€ Servicios" },
      ],
      columnasVisibles: columnasVisiblesPorDefecto(),
      resultado: {
        ofertas: [],
        numeroPaginas: 0,
        pagina: 1,
      },
    };
  },
  computed: {
    ...mapState(useSessionStore, ["usuarioId", "esAdmin", "esResponsable", "esPartner"]),
    ...mapState(useFiltrosOfertaFormStore, ["maestros", "mostrarFiltrosUsados", "filtrosUsados"]),
    ...mapWritableState(useFiltrosOfertaFormStore, ["filtros"]),
    botonesEstado: {
      get() {
        return this.maestros.estados.map((estado) => ({
          caption: estado.text,
          state: this.filtros.estados.includes(estado.value),
        }));
      },
      set(value) {
        if (value) {
          this.filtros.estados.push(value);
        } else {
          const index = this.filtros.estados.indexOf(value);
          this.filtros.estados.splice(index, 1);
        }
      },
    },
  },
  watch: {
    "$route.params": {
      handler: function () {
        this.listar();
      },
      deep: true,
      immediate: true,
    },
    columnasVisibles() {
      this.guardarColumnasVisibles();
    },
  },
  methods: {
    ...mapActions(useSessionStore, ["setUltimaOfertaModificada"]),
    ...mapActions(useFiltrosOfertaFormStore, ["limpiarFiltros"]),
    toggleColumnConfig() {
      this.showColumnConfig = !this.showColumnConfig;
    },
    toggleFiltros() {
      this.mostrarFiltros = !this.mostrarFiltros;
    },
    async listar() {
      const filtrosOfertaFormStore = useFiltrosOfertaFormStore();
      const params = Object.assign({}, filtrosOfertaFormStore.filtrosAplicables);

      if ("orden" in this.$route.query) {
        params.orden = this.$route.query.orden;
        params.dir = this.$route.query.dir;
      }
      if ("pagina" in this.$route.query) {
        params.pagina = this.$route.query.pagina;
      }

      this.resultado = await ofertasApi.listar(params);
    },
    cargarProductividad() {
      const filtrosOfertaFormStore = useFiltrosOfertaFormStore();
      const filtrosAplicables = filtrosOfertaFormStore.filtrosAplicables;
      return this.$refs.productividad.cargarProductividad(filtrosAplicables);
    },
    ofertaCambiada(id) {
      this.setUltimaOfertaModificada(id);
      return Promise.all([this.listar(), this.cargarProductividad()]);
    },
    cargarColumnasVisibles() {
      const cols = localStorage.getItem("ofertas.columnas.visibles");
      if (cols) {
        this.columnasVisibles = JSON.parse(cols);
      }
    },
    guardarColumnasVisibles() {
      localStorage.setItem("ofertas.columnas.visibles", JSON.stringify(this.columnasVisibles));
    },
    resetearColumnasVisibles() {
      this.columnasVisibles = columnasVisiblesPorDefecto();
      this.guardarColumnasVisibles();
    },
    filtrosCambiados() {
      const filtrosOfertaFormStore = useFiltrosOfertaFormStore();
      return Promise.all([
        this.listar(),
        this.cargarProductividad(),
        filtrosOfertaFormStore.actualizarFiltrosAplicables(),
      ]);
    },
  },
  mounted() {
    this.cargarColumnasVisibles();
    // this.listar();
    this.cargarProductividad();
  },
  async beforeRouteEnter(to, from, next) {
    const filtrosOfertaFormStore = useFiltrosOfertaFormStore();
    await filtrosOfertaFormStore.cargarMaestros();

    next();
  },
};
</script>
